import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { ReportLayoutWidget } from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { useReportSourceCatalog } from "@/app/app/_/views/ReportsView/reportFilterDefinitions.hooks";
import {
  createWidgetFromPresentation,
  createWidgetFromSource,
  isPresentationDragId,
  isSourceDragId,
  presentationEntryFromDragId,
  sourceNameFromDragId,
  type PresentationCatalogEntry,
  type ReportSource,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";

export const DASHBOARD_CANVAS_DROP_ID = "dashboard-canvas";

/**
 * The pointer sensor for a mouse or a pen alone: a finger is the touch
 * sensor's, which waits for it to hold still, so one drawn across the report
 * scrolls it rather than picking up whatever card it started on.
 */
class FinePointerSensor extends PointerSensor {
  static activators = PointerSensor.activators.map((activator) => ({
    ...activator,
    handler: (
      ...args: Parameters<(typeof PointerSensor.activators)[number]["handler"]>
    ) =>
      args[0].nativeEvent.pointerType !== "touch" && activator.handler(...args),
  }));
}

export function isCatalogDragId(id: string) {
  return isSourceDragId(id) || isPresentationDragId(id);
}

export type ReportsViewAssemblyDragSession = {
  source?: ReportSource;
  presentation?: PresentationCatalogEntry;
  widget: ReportLayoutWidget;
};

type ReportsViewAssembleDndHandlers = {
  onAssemblyDragStart?: (session: ReportsViewAssemblyDragSession) => void;
  onAssemblyDragOver?: (event: DragOverEvent) => void;
  onAssemblyDragEnd?: (event: DragEndEvent) => void;
  onAssemblyDragCancel?: () => void;
  onSortableDragStart?: (event: DragStartEvent) => void;
  onSortableDragOver?: (event: DragOverEvent) => void;
  onSortableDragEnd?: (event: DragEndEvent) => void;
  onSortableDragCancel?: () => void;
  renderAssemblyOverlay?: (
    session: ReportsViewAssemblyDragSession,
  ) => ReactNode;
  /** A widget taken from the catalogue by a tap rather than a drag. */
  onAssemblyAdd?: (widget: ReportLayoutWidget) => void;
};

type ReportsViewAssembleDndContextValue = {
  registerHandlers: (handlers: ReportsViewAssembleDndHandlers | null) => void;
  assemblySession: ReportsViewAssemblyDragSession | null;
  /**
   * Adds what a catalogue tile is to the end of the report, as dropping it
   * there would: a tap's way in, where a finger cannot drag it across.
   */
  addFromCatalog: (id: string) => void;
};

const ReportsViewAssembleDndContext =
  createContext<ReportsViewAssembleDndContextValue | null>(null);

/** The add a catalogue tile is tapped into; ``null`` outside a report. */
export function useReportsViewAddFromCatalog() {
  return useContext(ReportsViewAssembleDndContext)?.addFromCatalog ?? null;
}

export function useReportsViewAssembleDndRegistration(
  handlers: ReportsViewAssembleDndHandlers,
) {
  const ctx = useContext(ReportsViewAssembleDndContext);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useLayoutEffect(() => {
    if (!ctx) return;
    const wrapped: ReportsViewAssembleDndHandlers = {
      onAssemblyDragStart: (session) =>
        handlersRef.current.onAssemblyDragStart?.(session),
      onAssemblyDragOver: (event) =>
        handlersRef.current.onAssemblyDragOver?.(event),
      onAssemblyDragEnd: (event) =>
        handlersRef.current.onAssemblyDragEnd?.(event),
      onAssemblyDragCancel: () => handlersRef.current.onAssemblyDragCancel?.(),
      onSortableDragStart: (event) =>
        handlersRef.current.onSortableDragStart?.(event),
      onSortableDragOver: (event) =>
        handlersRef.current.onSortableDragOver?.(event),
      onSortableDragEnd: (event) =>
        handlersRef.current.onSortableDragEnd?.(event),
      onSortableDragCancel: () => handlersRef.current.onSortableDragCancel?.(),
      renderAssemblyOverlay: (session) =>
        handlersRef.current.renderAssemblyOverlay?.(session),
      onAssemblyAdd: (widget) => handlersRef.current.onAssemblyAdd?.(widget),
    };
    ctx.registerHandlers(wrapped);
    return () => ctx.registerHandlers(null);
  }, [ctx]);
}

function buildSessionFromDrag(
  activeId: string,
  pending: ReportsViewAssemblyDragSession | null,
  catalogue: readonly ReportSource[],
): ReportsViewAssemblyDragSession | null {
  const widgetId = `widget-${Math.random().toString(36).slice(2, 10)}`;

  if (isSourceDragId(activeId)) {
    const source = catalogue.find(
      (entry) => entry.name === sourceNameFromDragId(activeId),
    );
    if (!source) return null;
    if (pending?.presentation) {
      return {
        source,
        presentation: pending.presentation,
        widget: createWidgetFromSource(
          source,
          widgetId,
          pending.presentation.kind,
        ),
      };
    }
    return { source, widget: createWidgetFromSource(source, widgetId) };
  }

  if (isPresentationDragId(activeId)) {
    const presentation = presentationEntryFromDragId(activeId);
    if (!presentation) return null;
    if (pending?.source) {
      return {
        source: pending.source,
        presentation,
        widget: createWidgetFromSource(
          pending.source,
          widgetId,
          presentation.kind,
        ),
      };
    }
    return {
      presentation,
      widget: createWidgetFromPresentation(presentation.kind, widgetId),
    };
  }

  return null;
}

export function ReportsViewAssembleDndProvider({
  children,
}: {
  children: ReactNode;
}) {
  const handlersRef = useRef<ReportsViewAssembleDndHandlers | null>(null);
  const pendingSessionRef = useRef<ReportsViewAssemblyDragSession | null>(null);
  // What a widget may be of, as the server publishes it; held in a ref so the
  // drag handlers do not have to be rebuilt when it arrives.
  const catalogue = useReportSourceCatalog();
  const catalogueRef = useRef<readonly ReportSource[]>(catalogue);
  catalogueRef.current = catalogue;
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [assemblySession, setAssemblySession] =
    useState<ReportsViewAssemblyDragSession | null>(null);

  const registerHandlers = useCallback(
    (handlers: ReportsViewAssembleDndHandlers | null) => {
      handlersRef.current = handlers;
    },
    [],
  );

  // A mouse picks a card up as it moves; a finger only once it has held
  // still on it a moment - the way a phone lets things be rearranged - so a
  // finger drawn across the report scrolls it rather than moving a card.
  const sensors = useSensors(
    useSensor(FinePointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 8 },
    }),
  );

  const addFromCatalog = useCallback((id: string) => {
    const session = buildSessionFromDrag(id, null, catalogueRef.current);
    if (session) handlersRef.current?.onAssemblyAdd?.(session.widget);
  }, []);

  const collisionDetection = useCallback<CollisionDetection>((args) => {
    if (isCatalogDragId(String(args.active.id))) {
      return closestCenter(args);
    }
    const pointerHits = pointerWithin(args);
    if (pointerHits.length > 0) {
      const widgetHits = pointerHits.filter(
        (hit) =>
          hit.id !== args.active.id &&
          hit.id !== DASHBOARD_CANVAS_DROP_ID &&
          !isCatalogDragId(String(hit.id)),
      );
      if (widgetHits.length > 0) {
        return widgetHits;
      }
      return pointerHits.filter((hit) => hit.id !== args.active.id);
    }
    return rectIntersection(args);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = String(event.active.id);
    setActiveDragId(id);
    if (isCatalogDragId(id)) {
      const session = buildSessionFromDrag(
        id,
        pendingSessionRef.current,
        catalogueRef.current,
      );
      if (session) {
        setAssemblySession(session);
        handlersRef.current?.onAssemblyDragStart?.(session);
      }
      return;
    }
    setAssemblySession(null);
    handlersRef.current?.onSortableDragStart?.(event);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    if (isCatalogDragId(String(event.active.id))) {
      handlersRef.current?.onAssemblyDragOver?.(event);
      return;
    }
    handlersRef.current?.onSortableDragOver?.(event);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const activeId = String(event.active.id);
      if (isCatalogDragId(activeId)) {
        const session = assemblySession;
        if (session?.source && !session.presentation) {
          pendingSessionRef.current = session;
        } else if (session?.presentation && !session.source) {
          pendingSessionRef.current = session;
        } else {
          pendingSessionRef.current = null;
        }
        handlersRef.current?.onAssemblyDragEnd?.(event);
        setActiveDragId(null);
        setAssemblySession(null);
        return;
      }
      pendingSessionRef.current = null;
      handlersRef.current?.onSortableDragEnd?.(event);
      setActiveDragId(null);
      setAssemblySession(null);
    },
    [assemblySession],
  );

  const handleDragCancel = useCallback(() => {
    if (activeDragId && isCatalogDragId(activeDragId)) {
      handlersRef.current?.onAssemblyDragCancel?.();
    } else if (activeDragId) {
      handlersRef.current?.onSortableDragCancel?.();
    }
    setActiveDragId(null);
    setAssemblySession(null);
  }, [activeDragId]);

  const overlayContent = useMemo(() => {
    if (!activeDragId) return null;
    // A card on the page is carried itself (see the canvas), not a copy.
    if (isCatalogDragId(activeDragId) && assemblySession) {
      return (
        handlersRef.current?.renderAssemblyOverlay?.(assemblySession) ?? null
      );
    }
    return null;
  }, [activeDragId, assemblySession]);

  const value = useMemo(
    () => ({ registerHandlers, assemblySession, addFromCatalog }),
    [registerHandlers, assemblySession, addFromCatalog],
  );

  return (
    <ReportsViewAssembleDndContext.Provider value={value}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay dropAnimation={null}>{overlayContent}</DragOverlay>
      </DndContext>
    </ReportsViewAssembleDndContext.Provider>
  );
}
